/*
	TiltedCard component from React Bits
	Based on https://www.reactbits.dev/components/tilted-card
*/

import { useRef, useState, useEffect } from "react";
import { PhotoView } from 'react-photo-view';

const TiltedCard = ({
  imageSrc,
  altText = "Tilted card image",
  captionText = "",
  containerHeight = "600px",
  containerWidth = "100%",
  imageHeight = "300px",
  imageWidth = "300px",
  scaleOnHover = 1.1,
  rotateAmplitude = 14,
  showMobileWarning = true,
  showTooltip = true,
  displayOverlayContent = false,
  overlayContent = null,
  children,
  className = ""
}) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [transform, setTransform] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current || isMobile) return;

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    const rotateX = (mouseY / rect.height) * rotateAmplitude;
    const rotateY = -(mouseX / rect.width) * rotateAmplitude;
    
    setTransform(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(${scaleOnHover}, ${scaleOnHover}, ${scaleOnHover})`);
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTransform("");
  };

  // If children are provided, render as a wrapper
  if (children) {
    return (
      <div
        ref={cardRef}
        className={`relative overflow-hidden transition-transform duration-300 ease-out ${className}`}
        style={{
          height: containerHeight,
          width: containerWidth,
          transform: transform,
          transformStyle: 'preserve-3d',
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    );
  }

  // Default image-based TiltedCard
  return (
    <div className={`relative ${className}`} style={{ width: containerWidth, height: containerHeight }}>
      {showMobileWarning && isMobile && (
        <div className="absolute top-2 left-2 z-20 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
          Tilt effect disabled on mobile
        </div>
      )}
      
      <figure
        ref={cardRef}
        className="relative overflow-hidden rounded-lg shadow-lg transition-transform duration-300 ease-out cursor-pointer"
        style={{
          height: imageHeight,
          width: imageWidth,
          transform: transform,
          transformStyle: 'preserve-3d',
          margin: '0 auto'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <PhotoView src={imageSrc}>
          <img
            src={imageSrc}
            alt={altText}
            className="w-full h-full object-cover cursor-pointer"
            draggable={false}
          />
        </PhotoView>
        
        {displayOverlayContent && overlayContent && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white">
            {overlayContent}
          </div>
        )}
        
        {showTooltip && captionText && (
          <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white p-4 text-sm">
            {captionText}
          </figcaption>
        )}
      </figure>
    </div>
  );
};

export default TiltedCard;